import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Building2, 
  Users, 
  FileText, 
  BarChart3,
  UserCheck,
  ArrowRight
} from 'lucide-react';

const RoleSelector = ({ user, onRoleSelect }) => {
  const [selectedRole, setSelectedRole] = useState(null);

  const roleConfig = {
    broker: { 
      icon: Building2, 
      label: 'Broker', 
      description: 'Create and manage loan applications for clients',
      color: 'bg-blue-100 text-blue-800'
    },
    borrower: { 
      icon: Users, 
      label: 'Borrower', 
      description: 'Access your loan applications and documents',
      color: 'bg-green-100 text-green-800'
    },
    underwriter: { 
      icon: FileText, 
      label: 'Underwriter', 
      description: 'Review and process loan applications',
      color: 'bg-purple-100 text-purple-800'
    },
    jr_underwriter: {
      icon: FileText,
      label: 'Jr Underwriter',
      description: 'Review applications assigned to your underwriting team',
      color: 'bg-fuchsia-100 text-fuchsia-800'
    },
    admin: { 
      icon: BarChart3, 
      label: 'Admin', 
      description: 'Oversee system operations and manage users',
      color: 'bg-orange-100 text-orange-800'
    }
  };

  const handleContinue = () => {
    if (selectedRole) {
      onRoleSelect(selectedRole);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_broker-borrow-hub/artifacts/96jtqhbt_QuidityDirect%20Logo.png" 
              alt="Quidity Direct" 
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Welcome, {user.name}!</h1>
          <p className="text-slate-600 mb-4">You have multiple roles. Please select how you'd like to access the system today.</p>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            <UserCheck className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700 font-medium">Your available roles:</span>
            <div className="flex gap-2">
              {user.roles.map((role) => (
                <Badge key={role} className={roleConfig[role]?.color}>
                  {roleConfig[role]?.label || role}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {user.roles.map((role) => {
            const config = roleConfig[role];
            const IconComponent = config?.icon || UserCheck;
            const isSelected = selectedRole === role;

            return (
              <Card 
                key={role}
                className={`cursor-pointer transition-all duration-200 ${
                  isSelected 
                    ? 'ring-2 ring-slate-500 border-slate-300 shadow-lg' 
                    : 'hover:shadow-lg border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setSelectedRole(role)}
              >
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className={`p-3 rounded-full ${isSelected ? 'bg-teal-600' : 'bg-slate-100'}`}>
                      <IconComponent className={`h-8 w-8 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
                    </div>
                  </div>
                  <CardTitle className="text-slate-800">{config?.label || role}</CardTitle>
                  <CardDescription className="text-slate-600">
                    {config?.description || `Access ${role} features`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isSelected ? (
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Selected
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="w-full hover:bg-slate-50"
                    >
                      Select {config?.label || role}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {selectedRole && (
          <div className="text-center">
            <Button 
              onClick={handleContinue}
              size="lg"
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3"
            >
              <ArrowRight className="h-5 w-5 mr-2" />
              Continue as {roleConfig[selectedRole]?.label || selectedRole}
            </Button>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500">
            You can switch between your roles anytime using the role selector in the dashboard header.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelector;